import { ReactNode, useEffect, useReducer, ChangeEvent } from 'react';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import clsx from 'clsx';
import { EMAIL_REGEX } from 'constants/constants';
import CEFMessage from 'components/cef_message/cef_message';
import ToolTip from 'components/ui-components/ToolTip';
import { screenSize } from 'lib/utils';
import { grey70, grey50 } from 'constants/variables';
import useErrorPush from 'utils/useErrorPush';
import { InfoIcon, CloseWhite, WarningTriangle } from 'assets/icons';
import { reducer, initialState } from './reducers/delivery_details_reducer';
import type {
  DeliveryDetailsProps,
  DeliveryDay,
  DeliveryService,
  ApprovalGroup,
} from './types/delivery';

import {
  checkoutEventsGA4,
  checkoutEventsUA,
} from '../../data_layer/ga_checkout_events';

import warningIcon from '../../../assets/images/shared/icons/info.svg';

interface DeliveryDetailsMessageProps {
  className?: string;
  children: ReactNode;
}

const DeliveryDetailsMessage = ({
  className,
  children,
  ...rest
}: DeliveryDetailsMessageProps) => (
  <p className={clsx('cef-message mt-0', className)} {...rest}>
    {children}
  </p>
);

const DeliveryDetails = ({
  delivery_options: deliveryOptions,
  email: initialEmail,
  signed_in: signedIn,
  accountTelephone,
  guestTelephone,
  purchaseOrderRequired,
  basketHasBackOrderItems,
  setSplitBasketTelephone,
  current_step: currentStep,
  nextStepText,
  processingState,
  step,
  deliveryMessaging,
  basketSummary: {
    dangerous_goods: dangerousGoods,
    premium_carrier_dangerous_goods_message: dangerousGoodsMessage,
  },
  setDeliveryDetailsUrl,
  setLoadingState,
  showDetailErrors,
  setCurrentStep,
  getSummary,
  nextStep,
  configGenericError,
  toggleGenericError,
  validatePurchaseOrderMaskUrl,
  addressMode,
  basketSummary,
  clearDeliveryService,
  orderApprovals: {
    required: orderApprovalsRequired,
    groups: orderApprovalGroups,
    groupId: selectedGroupId,
  },
}: DeliveryDetailsProps) => {
  const createOrderApprovalsState = (groupId: number | null): ApprovalGroup => {
    const selectedGroupInfo =
      orderApprovalGroups.length === 1
        ? orderApprovalGroups[0]
        : orderApprovalGroups.find(group => group[0] === groupId) || [];

    return {
      id: selectedGroupInfo[0] || null,
      name: selectedGroupInfo[1] || '',
    };
  };

  const [state, dispatch] = useReducer(reducer, {
    ...initialState,
    telephone: signedIn ? accountTelephone : guestTelephone,
    email: initialEmail,
    selectedOrderApprovalGroup: createOrderApprovalsState(selectedGroupId),
  });

  const {
    telephone,
    charsLeft,
    overCharCount,
    purchaseOrder,
    email,
    errors,
    selectedDate,
    selectedDeliveryService,
    panelError,
    uniqueDeliveryDays,
    filteredServices,
    instructions,
    selectedOrderApprovalGroup,
  } = state;

  const debounceUseErrorPush = AwesomeDebouncePromise(useErrorPush, 1500);

  const logPurchaseOrderError = (errorMessage: string) => {
    debounceUseErrorPush(errorMessage, window.dataLayer);
    dispatch({
      type: 'UPDATE_ERRORS',
      payload: { message: errorMessage, type: 'purchaseOrder' },
    });
  };

  const validatePurchaseOrderMask = () => {
    if (purchaseOrderRequired) {
      if (purchaseOrder !== '') {
        dispatch({ type: 'CLEAR_ERRORS', payload: 'purchaseOrder' });
        $.ajax({
          url: validatePurchaseOrderMaskUrl,
          method: 'GET',
          dataType: 'json',
          data: {
            purchase_order: purchaseOrder,
          },
          success: () => {
            dispatch({
              type: 'SET_SHOW_PANEL_ERROR',
              payload: false,
            });
          },
          error: () => {
            dispatch({
              type: 'SET_SHOW_PANEL_ERROR',
              payload: true,
            });
            logPurchaseOrderError(
              'Purchase order does not match your pre-agreed format.',
            );
          },
        });
      } else {
        logPurchaseOrderError('A Purchase Order must be provided');
      }
    }
  };

  const validateDeliveryDetails = () => {
    if (signedIn) {
      dispatch({ type: 'CLEAR_ERRORS', payload: 'email' });
      dispatch({ type: 'CLEAR_ERRORS', payload: 'telephone' });

      if (!EMAIL_REGEX.test(email)) {
        const emailErrorMessage = 'E-Mail Invalid';
        useErrorPush(emailErrorMessage, window.dataLayer);
        dispatch({
          type: 'UPDATE_ERRORS',
          payload: { message: emailErrorMessage, type: 'email' },
        });
      }

      if (!telephone || telephone?.length === 0) {
        const tradeTelephoneErrorMessage =
          'Please enter a Telephone Number, this will only be used to provide updates on this order';
        useErrorPush(tradeTelephoneErrorMessage, window.dataLayer);
        dispatch({
          type: 'UPDATE_ERRORS',
          payload: { message: tradeTelephoneErrorMessage, type: 'telephone' },
        });
      }
    }

    return errors.length === 0;
  };

  const handleChangeInstructions = (e: ChangeEvent<HTMLTextAreaElement>) => {
    dispatch({
      type: 'HANDLE_CHANGE_INSTRUCTIONS',
      payload: { instructions: e.target.value },
    });
  };

  const handleChangeEmail = (e: ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: 'HANDLE_CHANGE_EMAIL',
      payload: e.target.value,
    });
  };

  const handleChangePurchaseOrder = (e: ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: 'HANDLE_CHANGE_PURCHASE_ORDER',
      payload: e.target.value,
    });

    if (e.target.value === '') {
      logPurchaseOrderError('A Purchase Order must be provided');
    } else {
      dispatch({ type: 'CLEAR_ERRORS', payload: 'purchaseOrder' });
    }
  };

  const handleChangeTelephone = (e: ChangeEvent<HTMLInputElement>) => {
    dispatch({
      type: 'HANDLE_CHANGE_TELEPHONE',
      payload: e.target.value,
    });

    if (setSplitBasketTelephone) {
      setSplitBasketTelephone(e.target.value);
    }
  };

  const setSelectedDeliveryAddress = (selectedService: DeliveryService) => {
    const service = filteredServices.filter(
      s => s.delivery_service_id === selectedService.delivery_service_id,
    );

    dispatch({
      type: 'SET_SELECTED_DELIVERY_SERVICE',
      payload: service[0],
    });
  };

  const filterAvailableServicesForSelectedDate = (
    filteredDate: DeliveryDay,
  ) => {
    dispatch({
      type: 'SET_FILTERED_SERVICES_BY_DATE',
      payload: {
        selectedDate: filteredDate,
        filteredServices: deliveryOptions.filter(
          option => option.day === filteredDate.day,
        ),
        selectedDeliveryService: undefined,
      },
    });
  };

  const setDeliveryDetails = () => {
    setLoadingState(true);

    $.ajax({
      url: setDeliveryDetailsUrl,
      method: 'PUT',
      dataType: 'json',
      data: {
        delivery_service: selectedDeliveryService,
        email,
        telephone,
        purchase_order: purchaseOrder,
        instructions,
        approval_group_id: selectedOrderApprovalGroup.id,
      },
      success: () => {
        dispatch({ type: 'SET_FILTERED_SERVICES_BY_DATE' });

        showDetailErrors(false);
        setCurrentStep(nextStep);
        getSummary(); // getSummary will set the loading state to false
      },
      error: (data: any) => {
        setLoadingState(false);
        if (data.status === 500) {
          useErrorPush(data.responseText, window.dataLayer);
          configGenericError({
            title: 'Error',
            body: (
              <div>
                <p>Unfortunately something has gone wrong.</p>

                <p>
                  We are sorry for any inconvenience this may cause. Please try
                  again later. If the problem persists, please contact our
                  customer service team.
                </p>
              </div>
            ),
            errorButton: true,
            errorButtonTitle: 'Refresh Checkout',
            errorButtonAction: toggleGenericError,
            closeAction: toggleGenericError,
            reloadCheckoutAfterClose: true,
            reloadCheckoutAfterErrorButtonAction: true,
          });
        } else {
          showDetailErrors(true, data.responseText);
        }
      },
    });
  };

  const goToNextStep = () => {
    if (selectedDeliveryService) {
      const { day, description, price } = selectedDeliveryService;

      validatePurchaseOrderMask();

      switch (nextStep) {
        case 'PAYMENT':
          checkoutEventsUA({
            event: 'deliveryOptions',
            options: { day, description, price },
          });

          // TO DO: Add extra properties in Phase 2
          checkoutEventsGA4({
            event: 'add_shipping_info_ga4',
            options: {
              value: basketSummary.total,
              addressMode,
              day,
              description,
              price,
              products: basketSummary.lines,
            },
          });

          if (validateDeliveryDetails()) {
            setDeliveryDetails();
          }
          break;
        case 'COLLECTION_DETAILS':
          if (validateDeliveryDetails()) {
            setDeliveryDetails();
          }
          break;
        default:
      }
    }
  };

  const editThisStep = () => {
    // should we do a clear down as we're moving back and get updated delivery options?
    clearDeliveryService();
    setCurrentStep('DELIVERY_DETAILS');
  };

  const updateSelectedGroup = (groupName: string) => {
    orderApprovalGroups.forEach(group => {
      if (group[1] === groupName) {
        dispatch({
          type: 'SET_SELECTED_ORDER_APPROVAL_GROUP',
          payload: { id: group[0], name: group[1] },
        });
      }
    });
  };

  useEffect(() => {
    dispatch({
      type: 'SET_UNIQUE_DELIVERY_DAYS',
      payload: deliveryOptions.filter(
        (option, index, array) =>
          index === array.findIndex(o => o.day === option.day),
      ),
    });
  }, [deliveryOptions]);

  useEffect(() => {
    if (!signedIn)
      dispatch({
        type: 'HANDLE_CHANGE_TELEPHONE',
        payload: guestTelephone,
      });
  }, [guestTelephone]);

  useEffect(() => {
    validateDeliveryDetails();
  }, [telephone, email]);

  const showContinue =
    selectedDeliveryService &&
    ((signedIn && email !== '') ||
      (!signedIn && (errors.length === 0 || email === '')));

  return (
    <div
      className={clsx(
        'checkout-panel delivery-details-step',
        currentStep !== 'DELIVERY_DETAILS' && 'not-active',
        processingState && 'processing-payment',
      )}>
      <div className="checkout-header-title">
        <div className="checkout-header-title-left">
          {panelError && <i className="icon-info-sign" />}
          <strong>{step}.</strong> Delivery Options{' '}
          {(currentStep === 'PAYMENT' ||
            currentStep === 'COLLECTION_DETAILS') && <i className="icon-ok" />}
        </div>
        <div className="checkout-header-title-right">
          {(currentStep === 'PAYMENT' ||
            currentStep === 'COLLECTION_DETAILS') && (
            <strong className="pull-right edit" onClick={editThisStep}>
              Edit
            </strong>
          )}
        </div>
      </div>

      <div className="checkout-sub-panel delivery-details">
        {basketHasBackOrderItems && (
          <DeliveryDetailsMessage
            className="cef-message--danger"
            data-testid="qa-back-order-message">
            <WarningTriangle className="mr-8" width={20} height={18} />
            Delivery dates for out of stock items can not be guaranteed
          </DeliveryDetailsMessage>
        )}
        {dangerousGoods && dangerousGoodsMessage && (
          <DeliveryDetailsMessage data-testid="qa-dangerous-goods-message">
            <img
              className="mr-8"
              src={warningIcon}
              alt=""
              width="20"
              height="18"
            />
            {dangerousGoodsMessage}
          </DeliveryDetailsMessage>
        )}
        <p>Please select a delivery date</p>

        <select
          onChange={e => {
            filterAvailableServicesForSelectedDate(JSON.parse(e.target.value));
          }}
          className="available--delivery-dates-select"
          defaultValue={selectedDate ? selectedDate.day : 'DEFAULT'}>
          <option value="DEFAULT" disabled>
            Select a delivery date
          </option>
          {uniqueDeliveryDays.map((d: DeliveryDay) => (
            <option key={`${d.day} ${d.description}`} value={JSON.stringify(d)}>
              {d.day}
            </option>
          ))}
        </select>

        <div className="available--delivery-dates">
          {uniqueDeliveryDays.map((d: DeliveryDay) => (
            <button
              key={`${d.day} ${d.description}`}
              type="button"
              onClick={() => {
                filterAvailableServicesForSelectedDate(d);
              }}
              className={clsx(
                selectedDate && selectedDate.day === d.day && 'selected',
              )}>
              {d.day}
            </button>
          ))}
        </div>

        {selectedDate &&
          selectedDate.day !== undefined &&
          selectedDate.approval_cutoff_date !== null && (
            <p className="delivery-details__warning">
              This order will need to be approved by{' '}
              <b>{selectedDate.approval_cutoff_date}</b> to be delivered on{' '}
              <b>{selectedDate.day}</b>
            </p>
          )}

        {filteredServices.length > 0 && (
          <>
            {deliveryMessaging?.premium_carrier_services && (
              <CEFMessage
                icon={warningIcon}
                html={deliveryMessaging.premium_carrier_services}
              />
            )}
            <p>Please select a delivery time</p>
          </>
        )}

        {filteredServices.length > 0 && (
          <select
            onChange={e => {
              setSelectedDeliveryAddress(JSON.parse(e.target.value));
            }}
            className="available--delivery-times-select"
            defaultValue="DEFAULT">
            <option value="DEFAULT" disabled>
              Select a delivery time
            </option>
            {filteredServices.map((d: DeliveryService) => (
              <option key={d.description} value={JSON.stringify(d)}>
                {d.description} - {d.price}
              </option>
            ))}
          </select>
        )}

        <div className="available--delivery-dates times-and-prices">
          {filteredServices.map((d: DeliveryService) => (
            <button
              key={d.description}
              type="button"
              onClick={() => {
                setSelectedDeliveryAddress(d);
              }}
              className={clsx(
                selectedDeliveryService &&
                  selectedDeliveryService.delivery_service_id ===
                    d.delivery_service_id &&
                  'selected',
              )}>
              {d.description} - {d.price}
            </button>
          ))}
        </div>

        {selectedDeliveryService && (
          <div>
            <hr />
            {!signedIn && (
              <div>
                <p>
                  <strong>Additional Information</strong>
                </p>
                <p>
                  An order confirmation will be sent to the following email
                  address:
                </p>
                <div className="fl-on fl-jc-sb ">
                  <div className="fl-fb-50">
                    <label>Email</label>
                    <p className="notes-reference-info">{email}</p>
                  </div>
                  <div className="fl-fb-50">
                    <label>Telephone</label>
                    <p className="notes-reference-info">{guestTelephone}</p>
                  </div>
                </div>
                <hr />
              </div>
            )}

            <p>
              <strong>Add Delivery Instructions/Order Notes/Cable Cuts</strong>
            </p>

            {overCharCount ? (
              <p className="char-count over-count">140 characters only</p>
            ) : (
              <p className="char-count">{charsLeft} characters remaining</p>
            )}

            <textarea
              onChange={handleChangeInstructions}
              value={instructions}
              placeholder="e.g. site contact details"
              maxLength={140}
            />

            {deliveryMessaging?.message && (
              <p className="notes-reference-info mb-20">
                <i className="icon-info-sign" />
                {deliveryMessaging.message}
              </p>
            )}

            <div className={clsx(!signedIn && 'hidden')}>
              <p>
                <strong>
                  An order confirmation will be sent to the following email
                  address:
                </strong>
              </p>
            </div>
            <div className={clsx('fl-on fl-jc-sb', !signedIn && 'hidden')}>
              <div className="fl-fb-50">
                <label htmlFor="delivery-email">
                  E-Mail <strong className="required">*</strong>
                </label>
                <input
                  id="delivery-email"
                  type="email"
                  onChange={handleChangeEmail}
                  onBlur={validateDeliveryDetails}
                  autoComplete="no"
                  value={email}
                />
              </div>
              <div className="fl-fb-50">
                <label htmlFor="delivery-telephone">
                  Telephone <strong className="required">*</strong>
                </label>
                <input
                  id="delivery-telephone"
                  type="tel"
                  onChange={handleChangeTelephone}
                  value={telephone}
                  autoComplete="no"
                />
              </div>
            </div>
            <div className="fl-on fl-jc-sb">
              <div className="fl-fb-50 pos-r purchase__order-wrapper">
                <div className="mb-5">
                  <span id="purchase-order" className="purchase-order-field">
                    {signedIn ? 'Purchase Order ' : 'Your Reference '}
                  </span>
                  {purchaseOrderRequired && (
                    <strong className="required">*</strong>
                  )}
                  {signedIn && (
                    <ToolTip
                      bgColor={grey70}
                      className="purchase__order-tooltip jc-fs ta-l"
                      toolTipIcon={
                        <InfoIcon
                          width={14}
                          height={14}
                          fillColor={grey50}
                          title="Purchase order help"
                        />
                      }
                      closeIcon={
                        screenSize() < 1025 && (
                          <CloseWhite width={14} height={14} />
                        )
                      }>
                      <p className="tooltip-content ff-body lh-1-3 nm">
                        This is the unique reference assigned to a purchase
                        order. If you do not use purchase orders, simply add
                        text as reference.
                      </p>
                    </ToolTip>
                  )}
                </div>
                <input
                  aria-labelledby="purchase-order"
                  type="text"
                  onChange={handleChangePurchaseOrder}
                  onBlur={validatePurchaseOrderMask}
                  value={purchaseOrder}
                  autoComplete="no"
                />
              </div>
              {orderApprovalsRequired && orderApprovalGroups.length > 1 && (
                <div className="fl-fb-50">
                  <label htmlFor="approval-groups">
                    Order Approval Group <strong className="required">*</strong>
                  </label>
                  <select
                    id="approval-groups"
                    className="width-100 p-10"
                    style={{
                      height: '42px',
                      border: '1px solid #999999',
                    }}
                    value={selectedOrderApprovalGroup.name || 'DEFAULT'}
                    onChange={event => {
                      updateSelectedGroup(event.target.value);
                    }}>
                    <option value="DEFAULT" disabled hidden>
                      Select approval group
                    </option>
                    {orderApprovalGroups.map(group => (
                      <option key={group[0]} value={group[1]}>
                        {group[1]}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {errors.length > 0 && (
              <ul className="errors">
                {errors.map(e => (
                  <li key={e.message}>{e.message}</li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>

      {showContinue && (
        <div className="checkout-sub-panel fl-on fl-sm-on next-step">
          <button
            type="button"
            onClick={goToNextStep}
            className="set-delivery-details-button next-panel ff-body fw-600">
            Continue to {nextStepText}
          </button>
        </div>
      )}
    </div>
  );
};

export default DeliveryDetails;
